// Handoff + Open Tickets. Single clean iOS list by default, with demo
// scenarios demoted to a "Scenarios" grouped list below so staff can
// still click through to see each state in the mockup.

import { useState } from "react";
import { fmtMoney, openTickets, staffById } from "./fixtures";
import { Chevron, MockShell, Row, TouchButton } from "./MockShell";
import { showToast } from "./toast";

type View = "list" | "send" | "locked" | "qr";

type Filter = "all" | "mine" | "attention";

const filters: { id: Filter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "mine", label: "Mine" },
  { id: "attention", label: "Needs attention" },
];

const statusDot = {
  cart: "bg-sky-500",
  parked: "bg-slate-400",
  "waiting on buyer": "bg-sky-500",
  "nics delayed": "bg-amber-500",
  "sent to counter": "bg-[#052460]",
  "ready for pickup": "bg-emerald-500",
} as const;

const statusLabel = {
  cart: "In cart",
  parked: "Parked",
  "waiting on buyer": "Waiting on buyer",
  "nics delayed": "NICS delayed",
  "sent to counter": "Sent to counter",
  "ready for pickup": "Ready for pickup",
} as const;

export function Handoff() {
  const [view, setView] = useState<View>("list");
  const [filter, setFilter] = useState<Filter>("all");

  if (view === "send") return <SendScenario onBack={() => setView("list")} />;
  if (view === "locked") return <LockedScenario onBack={() => setView("list")} />;
  if (view === "qr") return <QrScenario onBack={() => setView("list")} />;

  const tickets = openTickets.filter((t) => {
    if (filter === "mine") return t.openedBy === "s-jake" || t.salesperson === "s-jake";
    if (filter === "attention")
      return t.status === "nics delayed" || t.status === "ready for pickup";
    return true;
  });

  return (
    <MockShell title="Open tickets">
      <div className="max-w-3xl mx-auto w-full px-4 sm:px-6 pb-10">
        <FilterSegmented value={filter} onChange={setFilter} />

        <div className="mt-5 bg-white rounded-2xl overflow-hidden">
          {tickets.map((t) => {
            const salesperson = staffById(t.salesperson)?.name;
            const editing =
              t.beingEditedBy && t.beingEditedBy !== "s-jake"
                ? staffById(t.beingEditedBy)?.name
                : null;
            const selfEditing = t.beingEditedBy === "s-jake";
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => showToast(`Would open ${t.label}`)}
                className="w-full text-left flex items-start gap-3 px-4 py-3.5 hairline last:shadow-none active:bg-slate-50"
              >
                <span
                  className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${statusDot[t.status]}`}
                  aria-hidden="true"
                />
                <span className="flex-1 min-w-0">
                  <span className="flex items-baseline justify-between gap-3">
                    <span className="text-[15px] font-medium text-slate-900 break-words min-w-0">
                      {t.label}
                    </span>
                    <span className="text-[15px] font-medium tabular-nums text-slate-900 shrink-0">
                      {fmtMoney(t.totalCents)}
                    </span>
                  </span>
                  {t.phone && (
                    <span className="block text-[13px] text-slate-500 break-words mt-0.5">
                      {t.phone}
                    </span>
                  )}
                  {t.email && (
                    <span className="block text-[13px] text-slate-500 break-words">{t.email}</span>
                  )}
                  <span className="block text-[13px] text-slate-500 break-words mt-0.5">
                    {t.linesSummary}
                  </span>
                  <span className="block text-[12px] text-slate-400 mt-1 break-words">
                    {statusLabel[t.status]} · {salesperson} · {t.lastActivity}
                  </span>
                  {selfEditing && (
                    <span className="block text-[12px] text-sky-700 mt-1">
                      Editing on this device
                    </span>
                  )}
                  {editing && (
                    <span className="block text-[12px] text-amber-700 mt-1">
                      Being edited by {editing}
                    </span>
                  )}
                </span>
                <Chevron className="text-slate-300 mt-1.5" />
              </button>
            );
          })}
        </div>

        <div className="mt-8 text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-1 pb-2">
          Demo scenarios
        </div>
        <div className="bg-white rounded-2xl overflow-hidden">
          <Row
            title="Send a ticket to another counter"
            subtitle="Hand off to a counter or a specific person"
            chevron
            onClick={() => setView("send")}
          />
          <Row
            title="Locked by another device"
            subtitle="Ticket is being edited elsewhere"
            chevron
            onClick={() => setView("locked")}
          />
          <Row
            title="QR to another device"
            subtitle="Open the same ticket on another iPad"
            chevron
            onClick={() => setView("qr")}
          />
        </div>
      </div>
    </MockShell>
  );
}

function FilterSegmented({ value, onChange }: { value: Filter; onChange: (v: Filter) => void }) {
  return (
    <div className="inline-flex gap-1 p-1 rounded-full bg-slate-200/70 w-full">
      {filters.map((f) => (
        <button
          key={f.id}
          type="button"
          onClick={() => onChange(f.id)}
          className={`flex-1 min-h-9 rounded-full text-[14px] font-medium transition ${
            value === f.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-600"
          }`}
        >
          {f.label}
        </button>
      ))}
    </div>
  );
}

function BackLink({ onBack }: { onBack: () => void }) {
  return (
    <button
      type="button"
      onClick={onBack}
      className="text-[15px] text-[#007aff] inline-flex items-center gap-0.5 mb-3"
    >
      <span className="text-xl leading-none">‹</span>
      <span>Open tickets</span>
    </button>
  );
}

function SendScenario({ onBack }: { onBack: () => void }) {
  return (
    <MockShell title="Send ticket">
      <div className="max-w-2xl mx-auto w-full px-4 sm:px-6 pb-10">
        <BackLink onBack={onBack} />
        <div className="bg-white rounded-2xl p-5">
          <div className="text-[13px] text-slate-500 uppercase tracking-wider font-medium">
            Ticket
          </div>
          <div className="text-[20px] font-semibold tracking-tight mt-0.5">Walk-in · ammo</div>
          <div className="text-[13px] text-slate-500">9mm 124gr × 4 · {fmtMoney(9996)}</div>
        </div>

        <div className="mt-5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-1 pb-2">
          Send to
        </div>
        <div className="bg-white rounded-2xl overflow-hidden">
          <Row
            title="Counter 1"
            subtitle="Elliott · drawer open"
            trailing={
              <span className="text-[12px] text-[#007aff] font-medium shrink-0">Recommended</span>
            }
            chevron
            mock="Would send to Counter 1"
          />
          <Row
            title="Counter 2"
            subtitle="No one signed in"
            chevron
            mock="Would send to Counter 2"
          />
          <Row
            title="Next available counter"
            subtitle="Chime at the next free counter"
            chevron
            mock="Would queue to next available counter"
          />
          <Row
            title="A specific person"
            subtitle="Push to their iPhone"
            chevron
            mock="Would open staff picker"
          />
        </div>
      </div>
    </MockShell>
  );
}

function LockedScenario({ onBack }: { onBack: () => void }) {
  return (
    <MockShell title="Locked ticket">
      <div className="max-w-2xl mx-auto w-full px-4 sm:px-6 pb-10">
        <BackLink onBack={onBack} />
        <div className="bg-amber-50 rounded-2xl p-5">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-full bg-amber-200 text-amber-900 grid place-items-center font-semibold">
              JM
            </div>
            <div className="min-w-0">
              <div className="font-semibold text-amber-900 tracking-tight truncate">
                Jake M. · Floor iPad 2
              </div>
              <div className="text-[13px] text-amber-800/80">Started 2 minutes ago</div>
            </div>
          </div>
          <p className="text-[14px] text-amber-900/80 mt-4">
            Only one device can edit a ticket at a time. Ask Jake to send it over, or take over if
            you have manager permission.
          </p>
        </div>

        <div className="mt-5 bg-white rounded-2xl overflow-hidden">
          <Row title="Request from Jake" subtitle="Sends a ping" chevron mock="Would ping Jake" />
          <Row
            title="Take over"
            subtitle="Manager only · logged in audit trail"
            destructive
            chevron
            mock="Would take over the ticket"
          />
        </div>
      </div>
    </MockShell>
  );
}

function QrScenario({ onBack }: { onBack: () => void }) {
  return (
    <MockShell title="QR handoff">
      <div className="max-w-2xl mx-auto w-full px-4 sm:px-6 pb-10">
        <BackLink onBack={onBack} />
        <div className="bg-white rounded-2xl p-6">
          <div className="text-[13px] text-slate-500 uppercase tracking-wider font-medium">
            Ticket
          </div>
          <div className="text-[20px] font-semibold tracking-tight mt-0.5">
            Fake Name Three — transfer pickup
          </div>
          <div className="text-[13px] text-slate-500">Sig P365 · {fmtMoney(10000)}</div>

          <div className="mt-6 flex flex-col items-center gap-3">
            <div className="bg-white rounded-2xl p-5 ring-1 ring-slate-200">
              <QrPlaceholder />
            </div>
            <div className="text-[13px] text-slate-500 text-center">
              Scan from another device to open the same work
            </div>
          </div>

          <div className="mt-6">
            <TouchButton size="lg" full variant="secondary" mock="Would send a link instead">
              Send a link instead
            </TouchButton>
          </div>
        </div>
      </div>
    </MockShell>
  );
}

function QrPlaceholder() {
  return (
    <div className="grid grid-cols-9 gap-0.5 w-40 h-40">
      {Array.from({ length: 81 }, (_, i) => {
        const row = Math.floor(i / 9);
        const col = i % 9;
        const seed = (i * 7 + 3) % 11;
        const on = seed < 5;
        return <div key={`qr-${row}-${col}`} className={on ? "bg-slate-900" : "bg-transparent"} />;
      })}
    </div>
  );
}
